// Document/page editor with live preview, per-page AI editing, inline text edits,
// chart regeneration, and images.

import React from "react";
import type { AssetRef, Chart, Page, ProjectDetail } from "@asmg/shared";
import { api } from "../api/client";
import { Button, ErrorBanner } from "../components/ui";
import { ChartView } from "../components/ChartView";
import { ImagePanel } from "../components/ImagePanel";
import { ExportPanel } from "../components/ExportPanel";

function pageLabel(p: Page, idx: number): string {
  if (p.pageKind === "cover") return "Cover";
  if (p.pageKind === "toc") return "Contents";
  if (p.pageKind === "bonus") return `Bonus ${idx}`;
  return `Unit ${idx}`;
}

export function DocumentEditor({ detail, onReload }: { detail: ProjectDetail; onReload: () => void }) {
  const [pages, setPages] = React.useState<Page[]>(() => [...detail.pages].sort((a, b) => a.ord - b.ord));
  const [charts, setCharts] = React.useState<Chart[]>(detail.charts);
  const [assets, setAssets] = React.useState<AssetRef[]>(detail.assets);
  const [selectedId, setSelectedId] = React.useState<string>(pages[0]?.id ?? "");

  const [instruction, setInstruction] = React.useState("");
  const [aiBusy, setAiBusy] = React.useState(false);
  const [aiError, setAiError] = React.useState<unknown>(null);

  const [editingHtml, setEditingHtml] = React.useState(false);
  const [draftHtml, setDraftHtml] = React.useState("");
  const [saveBusy, setSaveBusy] = React.useState(false);
  const [saveError, setSaveError] = React.useState<unknown>(null);

  React.useEffect(() => {
    setPages([...detail.pages].sort((a, b) => a.ord - b.ord));
    setCharts(detail.charts);
    setAssets(detail.assets);
  }, [detail]);

  const selected = pages.find((p) => p.id === selectedId) ?? pages[0];
  const pageCharts = charts.filter((c) => c.pageId === selected?.id);
  const pageAssets = assets.filter((a) => a.pageId === selected?.id);

  function replacePage(updated: Page) {
    setPages((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
  }

  async function applyAiEdit() {
    if (!selected || !instruction.trim()) return;
    setAiBusy(true);
    setAiError(null);
    try {
      const updated = await api.aiEditPage(detail.id, selected.id, instruction.trim());
      replacePage(updated); // success → no error UI
      setInstruction("");
    } catch (e) {
      setAiError(e); // failure → retry, content preserved
    } finally {
      setAiBusy(false);
    }
  }

  function startInlineEdit() {
    if (!selected) return;
    setDraftHtml(selected.contentHtml);
    setEditingHtml(true);
  }

  async function saveInlineEdit() {
    if (!selected) return;
    setSaveBusy(true);
    setSaveError(null);
    try {
      const updated = await api.inlineEditPage(detail.id, selected.id, draftHtml);
      replacePage(updated);
      setEditingHtml(false);
    } catch (e) {
      setSaveError(e);
    } finally {
      setSaveBusy(false);
    }
  }

  if (!selected) return <div className="card muted">No pages.</div>;

  return (
    <div>
      <ExportPanel projectId={detail.id} />

      <div className="page-list">
        {pages.map((p, i) => (
          <button
            key={p.id}
            className={`page-tab ${p.id === selectedId ? "active" : ""}`}
            onClick={() => {
              setSelectedId(p.id);
              setEditingHtml(false);
            }}
          >
            {pageLabel(p, i - 1)}
          </button>
        ))}
      </div>

      <div className="card">
        <label>AI edit this page</label>
        <div className="row">
          <input
            className="grow"
            placeholder="e.g. shorten the intro; make the tone more formal; add an example"
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
          />
          <Button onClick={applyAiEdit} loading={aiBusy} disabled={!instruction.trim()}>
            Apply
          </Button>
          <Button variant="ghost" onClick={editingHtml ? saveInlineEdit : startInlineEdit} loading={saveBusy}>
            {editingHtml ? "Save HTML" : "Edit HTML"}
          </Button>
        </div>
        <ErrorBanner error={aiError} onRetry={applyAiEdit} />
        <ErrorBanner error={saveError} onRetry={saveInlineEdit} />
      </div>

      {editingHtml ? (
        <textarea
          style={{ minHeight: 320, fontFamily: "monospace" }}
          value={draftHtml}
          onChange={(e) => setDraftHtml(e.target.value)}
        />
      ) : (
        <div className="preview">
          <div dangerouslySetInnerHTML={{ __html: selected.contentHtml }} />
          {pageCharts.map((c) => (
            <ChartView
              key={c.id}
              projectId={detail.id}
              chart={c}
              onRegenerated={(uc) => setCharts((prev) => prev.map((x) => (x.id === uc.id ? uc : x)))}
            />
          ))}
          <ImagePanel
            projectId={detail.id}
            pageId={selected.id}
            assets={pageAssets}
            onReplace={(a) => setAssets((prev) => prev.map((x) => (x.id === a.id ? a : x)))}
            onAdd={(a) => setAssets((prev) => [...prev, a])}
          />
        </div>
      )}

      <div className="row" style={{ marginTop: 12 }}>
        <Button variant="ghost" onClick={onReload}>
          Reload from server
        </Button>
      </div>
    </div>
  );
}
